import {Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, OnDestroy} from "@angular/core";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {UploaderOptions, UploadFile, UploadInput, UploadOutput} from "ngx-uploader";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {environment} from "../../../../environments/environment";
import {AuthService} from "../../../services/auth/auth-service";
import {ServiceControllerService} from "../../../services/service-controller/service-controller.service";
import {FileMetadata} from "../../../models/file-metadata";
import {ActivatedRoute} from "@angular/router";
import {Observable} from "rxjs";
import {take} from "rxjs/operators";

interface AcceptRule {
  raw: string;
  type: "mime" | "wildcard" | "extension";
  value: string;
}

@Component({
  selector: "file-handler",
  templateUrl: "./file-handler.component.html",
  styleUrls: ["./file-handler.component.scss"],
})
export class FileHandlerComponent implements OnChanges, OnInit, OnDestroy {

  @Input() public fileId: string | null = null;
  @Input() public serviceId?: string;
  @Input() public uploadPath?: string;
  // When set, file-handler treats `{baseUrl}/{apiBasePath}` as a self-contained
  // file resource: POST for upload, GET `{apiBasePath}/{id}` for metadata, and
  // `{apiBasePath}/{id}/binary` for download. Bypasses ServiceControllerService.
  @Input() public apiBasePath?: string;
  @Input() public label: string = "Provide example document";
  @Input() public description: string = "Example helps the AI learn to extract data and assess document authenticity";
  @Input() public accept: string = "*/*";
  // Opt-in: render a "Copy URL" button next to the trash that copies the
  // public download URL (the same one DS and other consumers use) to the
  // clipboard. Kept off by default so existing usages don't change.
  @Input() public showCopyUrl: boolean = false;
  @Output() public fileIdChanged = new EventEmitter<string | null>();
  @Output() public delete = new EventEmitter();

  public options: UploaderOptions = {concurrency: 1, maxUploads: 1, maxFileSize: environment.UPLOAD_MAX_FILE_SIZE};
  public uploadInput: EventEmitter<UploadInput> = new EventEmitter<UploadInput>();
  public files: UploadFile[] = [];
  public dragOver: boolean = false;
  public isUploading: boolean = false;
  public uploadProgress: number = 0;
  public fileName?: string;
  public fileSize?: number;
  public fileMetadata?: FileMetadata;
  public downloadUrl?: string;
  public downloadDisplayUrl?: string;
  public fileIconClass: string = "fal fa-file";
  public statusMessage?: { text: string; variant: "error" | "info" | "success" };
  public filePreviewUrl?: string;
  public fileUploadedAtText?: string;
  public allowedFormatsDescription?: string;

  private baseUrl: string = environment.BASE_URL;
  private readonly defaultFileIcon: string = "fal fa-file";
  private previewObjectUrl?: string;
  private readonly uploadDateFormatter = new Intl.DateTimeFormat(undefined, {year: "numeric", month: "short", day: "2-digit"});
  private acceptRules: AcceptRule[] = [];
  private allowAllFormats: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private serviceController: ServiceControllerService,
    private http: HttpClient,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {}

  public ngOnInit(): void {
    this.updateAcceptConstraints();
    if (!this.serviceId) {
      this.route.params.pipe(take(1)).subscribe((params) => {
        if (!this.serviceId && params?.serviceId && params.serviceId !== "new") {
          this.serviceId = params.serviceId;
          if (this.fileId) {
            this.populateFileData(this.fileId);
          }
        }
      });
    }
  }

  public ngOnDestroy(): void {
    this.revokePreviewObjectUrl();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    console.log("FileHandlerComponent ngOnChanges", changes);
    if (changes.accept) {
      this.updateAcceptConstraints();
    }
    if (changes.fileId) {
      const current = changes.fileId.currentValue as string | null;
      this.fileId = current;

      if (current) {
        this.fileName = this.deriveDisplayName(current);
        this.fileIconClass = this.resolveFileIcon(this.fileName);
        this.downloadUrl = this.buildDownloadUrl(current);
        this.downloadDisplayUrl = this.downloadUrl ? this.simplifyUrl(this.downloadUrl) : undefined;
        this.populateFileData(current);
      } else {
        this.resetFileInfo();
      }
    } else if (changes.serviceId && this.fileId) {
      this.populateFileData(this.fileId);
    }
  }

  public onUploadOutput(output: UploadOutput): void {
    switch (output.type) {
      case "addedToQueue":
        if (output.file) {
          if (!this.isFileAccepted(output.file)) {
            this.rejectFileByFormat(output.file);
            return;
          }
          this.files = [...this.files, output.file];
        }
        break;
      case "allAddedToQueue":
        if (this.files.length > 0) {
          this.startUpload();
        }
        break;
      case "start":
        this.isUploading = true;
        this.uploadProgress = 0;
        break;
      case "uploading":
        if (output.file) {
          this.updateFileInQueue(output.file);
          this.isUploading = true;
          this.uploadProgress = output.file.progress && output.file.progress.data ? output.file.progress.data.percentage ?? 0 : 0;
          this.setLocalFileInfo(output.file);
        }
        break;
      case "done":
        if (output.file) {
          this.handleUploadCompletion(output.file);
        }
        break;
      case "rejected":
        this.handleUploadRejection(output);
        break;
      case "dragOver":
        this.dragOver = true;
        break;
      case "dragOut":
      case "drop":
        this.dragOver = false;
        break;
      case "removed":
      case "cancelled":
        this.files = this.files.filter((file) => file !== output.file);
        this.resetUploadState();
        break;
      case "removedAll":
        this.files = [];
        this.resetUploadState();
        break;
      default:
        break;
    }
  }

  public triggerBrowse(input: HTMLInputElement): void {
    if (!this.isUploading) {
      input.click();
    }
  }

  public deleteFile(): void {
    const fileId = this.fileId;
    if (!fileId) {
      return;
    }
    this.delete.emit();
    this.resetUploadState();
  }

  public copyDownloadUrl(event: MouseEvent): void {
    event.stopPropagation();
    const url = this.downloadUrl;
    if (!url) {
      return;
    }
    this.writeToClipboard(url).then(
      () => this.toastr.success(this.translate.instant("URL copied to clipboard")),
      () => this.toastr.error(this.translate.instant("Could not copy URL")),
    );
  }

  // navigator.clipboard requires a secure context (https or localhost). Fall
  // back to a hidden textarea + execCommand("copy") so the button still works
  // on http deployments and older browsers.
  private writeToClipboard(text: string): Promise<void> {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise<void>((resolve, reject) => {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        const ok = document.execCommand("copy");
        ok ? resolve() : reject(new Error("execCommand returned false"));
      } catch (err) {
        reject(err);
      } finally {
        document.body.removeChild(textarea);
      }
    });
  }

  public formatSize(size?: number): string | undefined {
    if (size === undefined || size === null) {
      return undefined;
    }
    const units = ["B", "KB", "MB", "GB"];
    let index = 0;
    let value = size;
    while (value >= 1024 && index < units.length - 1) {
      value = value / 1024;
      index++;
    }
    return `${value.toFixed(value < 10 && index > 0 ? 2 : 0)} ${units[index]}`;
  }

  private startUpload(): void {
    const url = this.resolveUploadUrl();
    if (!url) {
      this.notifyError("Upload error", "No upload URL configured.");
      return;
    }

    this.authService.getToken().then((token) => {
      this.dismissStatusMessage();
      const event: UploadInput = {
        type: "uploadAll",
        url,
        headers: {Authorization: "Bearer " + token},
        method: "POST",
      };
      this.uploadInput.emit(event);
    });
  }

  private resolveUploadUrl(): string | null {
    if (this.uploadPath) {
      return this.concatenateUrl(this.uploadPath);
    }
    if (this.apiBasePath) {
      return this.concatenateUrl(this.apiBasePath);
    }
    if (this.serviceId) {
      return this.concatenateUrl(`service/${this.serviceId}/file`);
    }
    return this.concatenateUrl("service/file");
  }

  private concatenateUrl(path: string): string {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${this.baseUrl.replace(/\/$/, "")}${normalizedPath}`;
  }

  private populateFileData(fileId?: string | null): void {
    if (!fileId) {
      this.resetFileInfo();
      return;
    }

    const isUrlIdentifier = this.isAbsoluteUrl(fileId);
    const metadataEndpoint = this.resolveMetadataEndpoint(fileId);
    const request = this.createMetadataRequest(metadataEndpoint, isUrlIdentifier);
    const previousName = this.fileName;
    const previousSize = this.fileSize;

    console.log("---- getFile request", metadataEndpoint);
    if (!request) {
      this.notifyError("Get file error", "Unable to resolve metadata endpoint.");
      return;
    }
    request.subscribe(
      (data: any) => {
        console.log("---- getFile request", data);
        this.fileMetadata = data;
        const metadataName = this.extractMetadataFilename(data);
        this.fileName = this.resolveFileName(fileId, metadataName, previousName);
        this.fileSize = this.resolveFileSize(data) ?? previousSize;
        const uploadedAt = this.resolveUploadedAtText(data);
        this.fileUploadedAtText = uploadedAt;
        this.downloadUrl = this.buildDownloadUrl(fileId);
        this.downloadDisplayUrl = this.downloadUrl ? this.simplifyUrl(this.downloadUrl) : undefined;
        this.dismissStatusMessage();
        this.fileIconClass = this.resolveFileIcon(this.fileName, data?.contentType);
        const previewSource = this.resolvePreviewSource(data) || this.downloadUrl;
        const previewSet = this.refreshPreview({
          contentType: data?.contentType,
          sourceUrl: previewSource,
          fileName: this.fileName,
        });
        if (!previewSet) {
          this.clearPreview();
        }
      },
      (error) => {
        console.log("---- getFile request error", error);
        if (isUrlIdentifier && this.shouldTreatAsDirectFileUrl(fileId, error)) {
          this.populateDirectFileData(fileId);
          return;
        }
        this.notifyError("Get file error", error);
      },
    );
  }

  private setLocalFileInfo(file: UploadFile): void {
    this.fileUploadedAtText = undefined;
    if (file.nativeFile) {
      this.fileName = file.nativeFile.name;
      this.fileSize = file.nativeFile.size;
      const contentType = file.nativeFile.type || file.type;
      this.fileIconClass = this.resolveFileIcon(this.fileName, contentType);
      this.refreshPreview({
        contentType,
        nativeFile: file.nativeFile,
        fileName: this.fileName,
      });
    } else if (file.name) {
      this.fileName = file.name;
      this.fileSize = file.size;
      this.fileIconClass = this.resolveFileIcon(this.fileName, file.type);
      this.refreshPreview({
        contentType: file.type,
        fileName: this.fileName,
      });
    } else if (typeof file.response === "string") {
      this.fileName = this.deriveDisplayName(file.response);
      this.fileIconClass = this.resolveFileIcon(this.fileName);
      this.refreshPreview({
        sourceUrl: file.response,
        fileName: this.fileName,
      });
    } else if (file.response && typeof file.response === "object" && (file.response as any).name) {
      this.fileName = (file.response as any).name;
      this.fileIconClass = this.resolveFileIcon(this.fileName);
    }
  }

  private updateFileInQueue(file: UploadFile): void {
    this.files = this.files.map((existing) => existing.id === file.id ? file : existing);
  }

  private handleUploadCompletion(file: UploadFile): void {
    this.updateFileInQueue(file);
    this.isUploading = false;
    this.uploadProgress = 100;

    if (file.responseStatus && file.responseStatus >= 400) {
      this.notifyError("Upload error", typeof file.response === "string" ? file.response : "Unexpected error while uploading.");
      if (this.fileId) {
        this.populateFileData(this.fileId);
      } else {
        this.resetFileInfo();
      }
      this.clearUploadQueue();
      this.resetUploadState();
      return;
    }

    const response = file.response as any;
    const fileId = this.extractFileId(response);

    if (!fileId) {
      this.notifyError("Upload error", "Could not determine uploaded file id.");
      this.clearUploadQueue();
      this.resetUploadState();
      return;
    }

    this.fileId = fileId;
    this.downloadUrl = this.buildDownloadUrl(fileId);
    this.downloadDisplayUrl = this.downloadUrl ? this.simplifyUrl(this.downloadUrl) : undefined;
    this.populateFileData(fileId);
    this.fileIdChanged.emit(fileId);
    this.resetUploadState(true);
  }

  private extractFileId(response: any): string | null {
    if (!response) {
      return null;
    }
    if (typeof response === "string") {
      return response;
    }
    if (typeof response === "object" && response !== null) {
      if (response.fileId) {
        return response.fileId;
      }
      if (response.id) {
        return response.id;
      }
      if (response.response) {
        return this.extractFileId(response.response);
      }
    }
    return null;
  }

  private handleUploadRejection(output: UploadOutput): void {
    const file = output.file;
    let message = "File was rejected";
    if (file && file.size && file.size > environment.UPLOAD_MAX_FILE_SIZE) {
      message = "Upload file size exceeds the allowed limit.";
    }
    this.notifyError("Upload rejected", message);
    if (this.fileId) {
      this.populateFileData(this.fileId);
    }
    this.clearUploadQueue();
    this.resetUploadState();
  }

  private rejectFileByFormat(file: UploadFile): void {
    if (file.id) {
      this.uploadInput.emit({type: "remove", id: file.id});
    } else {
      this.uploadInput.emit({type: "removeAll"});
    }
    const fileName = file.nativeFile?.name || file.name;
    const allowedDescription = this.allowedFormatsDescription;
    const reasonParts: string[] = [];
    if (fileName) {
      reasonParts.push(`${fileName} is not an accepted file type.`);
    } else {
      reasonParts.push("Selected file type is not allowed.");
    }
    if (allowedDescription) {
      reasonParts.push(`Allowed formats: ${allowedDescription}.`);
    }
    this.notifyError("Upload rejected", reasonParts.join(" "));
  }

  private notifyError(title: string, error: any): void {
    const formatted = this.formatErrorMessage(title, error);
    this.showStatusMessage(formatted, "error");
  }

  private resetUploadState(keepFileInfo: boolean = false): void {
    this.isUploading = false;
    this.dragOver = false;
    this.uploadProgress = 0;
    this.files = [];
    if (!keepFileInfo && !this.fileId) {
      this.resetFileInfo();
    }
  }

  private resetFileInfo(): void {
    this.fileMetadata = undefined;
    this.fileName = undefined;
    this.fileSize = undefined;
    this.downloadUrl = undefined;
    this.downloadDisplayUrl = undefined;
    this.fileIconClass = this.defaultFileIcon;
    this.clearPreview();
    this.fileUploadedAtText = undefined;
  }

  private resolveFileIcon(fileName?: string, contentType?: string): string {
    const extension = this.extractExtension(fileName);
    const typeGroup = contentType ? contentType.split("/")[0] : undefined;

    if (contentType?.includes("pdf") || extension === "pdf") {
      return "fal fa-file-pdf";
    }

    if (typeGroup === "image" || this.isOneOf(extension, ["png", "jpg", "jpeg", "gif", "bmp", "svg", "webp", "tiff", "ico"])) {
      return "fal fa-file-image";
    }

    if (typeGroup === "audio" || this.isOneOf(extension, ["mp3", "wav", "ogg", "flac", "aac", "m4a"])) {
      return "fal fa-file-audio";
    }

    if (typeGroup === "video" || this.isOneOf(extension, ["mp4", "mov", "avi", "mkv", "webm", "wmv", "flv"])) {
      return "fal fa-file-video";
    }

    if (this.isOneOf(extension, ["doc", "docx", "odt", "rtf"])) {
      return "fal fa-file-word";
    }

    if (this.isOneOf(extension, ["xls", "xlsx", "csv", "ods"])) {
      return "fal fa-file-excel";
    }

    if (this.isOneOf(extension, ["ppt", "pptx", "odp"])) {
      return "fal fa-file-powerpoint";
    }

    if (this.isOneOf(extension, ["txt", "md", "log"])) {
      return "fal fa-file-alt";
    }

    if (this.isOneOf(extension, ["zip", "rar", "7z", "tar", "gz", "bz2", "xz"])) {
      return "fal fa-file-archive";
    }

    if (this.isOneOf(extension, ["json", "xml", "yml", "yaml", "html", "css", "js", "jsx", "ts", "tsx", "java", "py", "rb", "php", "c", "cpp", "cs", "go", "rs"])) {
      return "fal fa-file-code";
    }

    return this.defaultFileIcon;
  }

  private extractExtension(fileName?: string): string | undefined {
    if (!fileName) {
      return undefined;
    }
    const sanitized = fileName.split("?")[0];
    const segments = sanitized.split(".");
    if (segments.length <= 1) {
      return undefined;
    }
    return segments.pop()?.toLowerCase();
  }

  private isOneOf(value: string | undefined, list: string[]): boolean {
    if (!value) {
      return false;
    }
    return list.includes(value.toLowerCase());
  }

  public dismissStatusMessage(): void {
    this.statusMessage = undefined;
  }

  private showStatusMessage(text: string, variant: "error" | "info" | "success" = "info"): void {
    this.statusMessage = {text, variant};
  }

  public onDismissMessage(event: MouseEvent): void {
    event.stopPropagation();
    this.dismissStatusMessage();
  }

  private buildDownloadUrl(fileId: string): string {
    if (/^https?:\/\//i.test(fileId)) {
      return fileId;
    }
    if (this.apiBasePath === "media") {
      // Media binaries are served directly by RestHeart's GridFS gateway (db
      // `eregsystem`, bucket `media`), the same pattern used for the system
      // logo/favicon. The reverse proxy exposes `/restheart/` with permissive
      // read ACL so this URL works in <a target=_blank> and <img src> without
      // an Authorization header — clicking the filename actually opens the file.
      return this.concatenateRestHeartUrl(`eregsystem/media.files/${fileId}/binary`);
    }
    if (this.apiBasePath) {
      return this.concatenateUrl(`${this.apiBasePath}/${fileId}/binary`);
    }
    if (this.serviceId) {
      return this.concatenateUrl(`service/${this.serviceId}/file/${fileId}/binary`);
    }
    return this.concatenateUrl(`service/file/${fileId}/binary`);
  }

  private concatenateRestHeartUrl(path: string): string {
    const base = (environment.RESTHEART_URL || "").replace(/\/$/, "");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${base}${normalizedPath}`;
  }

  private populateDirectFileData(fileUrl: string): void {
    const previousName = this.fileName;
    const previousSize = this.fileSize;
    this.fileMetadata = undefined;
    this.fileName = previousName || this.deriveDisplayName(fileUrl);
    this.fileSize = previousSize;
    this.downloadUrl = fileUrl;
    this.downloadDisplayUrl = this.simplifyUrl(fileUrl);
    this.fileIconClass = this.resolveFileIcon(this.fileName);
    this.dismissStatusMessage();
    const previewSet = this.refreshPreview({
      sourceUrl: fileUrl,
      fileName: this.fileName,
    });
    if (!previewSet) {
      this.clearPreview();
    }
    this.fileUploadedAtText = undefined;
  }

  private shouldTreatAsDirectFileUrl(fileId: string, error: any): boolean {
    if (!fileId || !/^https?:\/\//i.test(fileId)) {
      return false;
    }

    if (error instanceof HttpErrorResponse) {
      const message = error.message || "";
      const parseFailure = /Http failure during parsing/i.test(message);
      if (parseFailure) {
        return true;
      }
      if (error.status === 200 && error.error instanceof ProgressEvent) {
        return true;
      }
      if (this.isBinaryResourceUrl(fileId) && error.status && error.status >= 400) {
        return true;
      }
    }

    return false;
  }

  private resolveFileName(fileId: string, metadataName?: string, fallbackName?: string): string {
    if (metadataName && metadataName.trim()) {
      return metadataName.trim();
    }
    if (fallbackName && fallbackName.trim()) {
      return fallbackName.trim();
    }
    return this.deriveDisplayName(fileId);
  }

  private clearUploadQueue(): void {
    this.uploadInput.emit({type: "removeAll"});
  }

  private createMetadataRequest(identifier: string, isUrl: boolean): Observable<any> | undefined {
    if (!identifier) {
      return undefined;
    }
    if (isUrl) {
      return this.serviceController.getFileFromURL(identifier);
    }
    if (this.apiBasePath) {
      return this.http.get(this.concatenateUrl(`${this.apiBasePath}/${identifier}`));
    }
    if (this.serviceId) {
      return this.serviceController.getServiceFile(this.serviceId, identifier);
    }
    return this.serviceController.getFile(identifier);
  }

  private resolveMetadataEndpoint(fileId: string): string {
    if (typeof fileId !== "string") {
      return fileId as string;
    }
    if (this.isBinaryResourceUrl(fileId)) {
      return this.stripBinarySuffix(fileId);
    }
    return fileId;
  }

  private isAbsoluteUrl(value: string): boolean {
    return /^https?:\/\//i.test(value);
  }

  private isBinaryResourceUrl(url: string): boolean {
    if (!url) {
      return false;
    }
    return /\/binary(?:\?|$)/.test(url);
  }

  private stripBinarySuffix(url: string): string {
    if (!url) {
      return url;
    }
    const [path, ...queryParts] = url.split("?");
    const trimmedPath = path.replace(/\/binary$/, "");
    if (queryParts.length === 0) {
      return trimmedPath;
    }
    return `${trimmedPath}?${queryParts.join("?")}`;
  }

  private extractMetadataFilename(metadata: any): string | undefined {
    const candidates = [
      metadata?.filename,
      metadata?.name,
      metadata?.originalName,
      metadata?.metadata?.filename,
      metadata?.metadata?.name,
      metadata?.metadata?.originalName,
    ];
    return this.firstNonEmptyString(candidates);
  }

  private resolveFileSize(metadata: any): number | undefined {
    const candidates = [
      metadata?.size,
      metadata?.sizeBytes,
      metadata?.length,
      metadata?.metadata?.size,
      metadata?.metadata?.sizeBytes,
      metadata?.metadata?.length,
      metadata?.metadata?.fileSize,
      metadata?.metadata?.contentLength,
    ];
    for (const candidate of candidates) {
      const value = this.extractNumericValue(candidate);
      if (value !== undefined) {
        return value;
      }
    }
    return undefined;
  }

  private resolveUploadedAtText(metadata: any): string | undefined {
    const date = this.extractUploadedAt(metadata);
    if (!date) {
      return undefined;
    }
    return `Uploaded ${this.uploadDateFormatter.format(date)}`;
  }

  private extractUploadedAt(metadata: any): Date | undefined {
    const candidates = [
      metadata?.metadata?.createdAt,
      metadata?.metadata?.uploadDate,
      metadata?.metadata?.uploadedAt,
      metadata?.uploadDate,
      metadata?.uploadedAt,
      metadata?.createdAt,
    ];
    for (const candidate of candidates) {
      const normalized = this.normalizeDate(candidate);
      if (normalized) {
        return normalized;
      }
    }
    return undefined;
  }

  private normalizeDate(value: any): Date | undefined {
    if (!value && value !== 0) {
      return undefined;
    }
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? undefined : value;
    }
    if (typeof value === "number") {
      const date = new Date(value);
      return isNaN(date.getTime()) ? undefined : date;
    }
    if (typeof value === "string") {
      const date = new Date(value);
      return isNaN(date.getTime()) ? undefined : date;
    }
    if (typeof value === "object") {
      if ("$date" in value) {
        return this.normalizeDate((value as any).$date);
      }
      if ("$numberLong" in value) {
        return this.normalizeDate(Number((value as any).$numberLong));
      }
      if ("$numberDouble" in value) {
        return this.normalizeDate(Number((value as any).$numberDouble));
      }
      if ("seconds" in value && "nanoseconds" in value) {
        const seconds = this.extractNumericValue((value as any).seconds) ?? 0;
        const nanos = this.extractNumericValue((value as any).nanoseconds) ?? 0;
        return this.normalizeDate(seconds * 1000 + nanos / 1_000_000);
      }
      if ("_seconds" in value && "_nanoseconds" in value) {
        const seconds = this.extractNumericValue((value as any)._seconds) ?? 0;
        const nanos = this.extractNumericValue((value as any)._nanoseconds) ?? 0;
        return this.normalizeDate(seconds * 1000 + nanos / 1_000_000);
      }
    }
    return undefined;
  }

  private extractNumericValue(raw: any): number | undefined {
    if (raw === undefined || raw === null) {
      return undefined;
    }
    if (typeof raw === "number") {
      return Number.isFinite(raw) ? raw : undefined;
    }
    if (typeof raw === "string") {
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    if (typeof raw === "object") {
      if ("$numberLong" in raw) {
        return this.extractNumericValue((raw as any).$numberLong);
      }
      if ("$numberDouble" in raw) {
        return this.extractNumericValue((raw as any).$numberDouble);
      }
      if ("$numberDecimal" in raw) {
        return this.extractNumericValue((raw as any).$numberDecimal);
      }
    }
    return undefined;
  }

  private firstNonEmptyString(values: Array<string | undefined | null>): string | undefined {
    for (const value of values) {
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
    return undefined;
  }

  private refreshPreview(options: {contentType?: string; sourceUrl?: string; nativeFile?: File; fileName?: string}): boolean {
    const {contentType, sourceUrl, nativeFile, fileName} = options;
    const name = fileName || this.fileName;
    const imageType = this.isImageFileType(name, contentType);

    if (imageType === false) {
      this.clearPreview();
      return false;
    }

    if (nativeFile) {
      this.setPreviewFromFile(nativeFile);
      return true;
    }

    if (imageType === true) {
      const resolved = sourceUrl || this.downloadUrl;
      if (resolved) {
        this.setPreviewFromUrl(resolved);
        return true;
      }
    }
    if (imageType === undefined && sourceUrl) {
      this.setPreviewFromUrl(sourceUrl);
      return true;
    }

    return false;
  }

  private setPreviewFromFile(file: File): void {
    this.revokePreviewObjectUrl();
    const objectUrl = URL.createObjectURL(file);
    this.previewObjectUrl = objectUrl;
    this.filePreviewUrl = objectUrl;
  }

  private setPreviewFromUrl(url: string): void {
    this.revokePreviewObjectUrl();
    this.filePreviewUrl = url;
  }

  private clearPreview(): void {
    this.revokePreviewObjectUrl();
    this.filePreviewUrl = undefined;
  }

  private revokePreviewObjectUrl(): void {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = undefined;
    }
  }

  private isImageFileType(fileName?: string, contentType?: string): boolean | undefined {
    if (contentType) {
      const typeGroup = contentType.split("/")[0];
      if (typeGroup === "image") {
        return true;
      }
      if (typeGroup) {
        return false;
      }
    }

    const extension = this.extractExtension(fileName);
    if (extension) {
      return this.isOneOf(extension, ["png", "jpg", "jpeg", "gif", "bmp", "svg", "webp", "tiff", "ico"]);
    }

    return undefined;
  }

  private resolvePreviewSource(metadata: any): string | undefined {
    if (!metadata || typeof metadata !== "object") {
      return undefined;
    }
    const candidates = [
      metadata.previewUrl,
      metadata.thumbnailUrl,
      metadata.downloadUrl,
      metadata.binaryUrl,
      metadata.signedUrl,
      metadata.fileUrl,
      metadata.url,
      metadata.link,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate;
      }
    }
    return undefined;
  }

  private deriveDisplayName(source: string): string {
    if (!source) {
      return "";
    }
    try {
      const url = new URL(source);
      const pathnameParts = url.pathname.split("/").filter(Boolean);
      if (pathnameParts.length > 0) {
        let candidate = pathnameParts[pathnameParts.length - 1];
        if (this.isGenericSegment(candidate) && pathnameParts.length > 1) {
          candidate = pathnameParts[pathnameParts.length - 2];
        }
        return decodeURIComponent(candidate);
      }
      return url.hostname;
    } catch (_) {
      const segments = source.split("/").filter(Boolean);
      let candidate = segments.pop() || source;
      if (this.isGenericSegment(candidate) && segments.length > 0) {
        candidate = segments.pop() as string;
      }
      return decodeURIComponent(candidate);
    }
  }

  private simplifyUrl(url: string): string {
    try {
      const parsed = new URL(url);
      const hostname = parsed.hostname;
      const path = parsed.pathname;
      const simplifiedPath = path && path !== "/" ? this.truncatePath(path) : "";
      return simplifiedPath ? `${hostname}${simplifiedPath}` : hostname;
    } catch (_) {
      return this.truncateUrl(url);
    }
  }

  private truncatePath(path: string): string {
    const maxLength = 60;
    if (path.length <= maxLength) {
      return path;
    }
    const segments = path.split("/").filter(Boolean);
    if (segments.length <= 1) {
      return `${path.slice(0, maxLength - 1)}…`;
    }
    const first = segments[0];
    const last = segments[segments.length - 1];
    return `/${first}/…/${last}`;
  }

  private isGenericSegment(segment: string): boolean {
    return ["binary", "download", "file", "files", "content", "attachment"].includes(segment.toLowerCase());
  }

  private formatErrorMessage(fallbackTitle: string, error: any): string {
    if (typeof error === "string") {
      return error;
    }

    const lines: string[] = [];
    let detailMessage: string | undefined;
    let urlLine: string | undefined;
    let titleLine: string | undefined;

    if (error instanceof HttpErrorResponse || (error && typeof error === "object" && "status" in error)) {
      const httpError = error as HttpErrorResponse | {status?: number; statusText?: string; message?: string; url?: string; error?: any};
      if (httpError.status) {
        const statusText = httpError.statusText ? httpError.statusText : "";
        titleLine = `HTTP ${httpError.status}${statusText ? ` ${statusText}` : ""}`.trim();
      }

      const inner = this.extractInnerMessage(httpError);
      const normalized = this.normalizeInnerMessage(inner);
      if (normalized?.message) {
        detailMessage = normalized.message;
      }
      if (normalized?.url) {
        urlLine = this.formatErrorUrl(normalized.url);
      }

      if (!urlLine && httpError.url) {
        urlLine = this.formatErrorUrl(httpError.url);
      }
    } else if (error && typeof error === "object") {
      const raw = error.message || error.error;
      const normalized = this.normalizeInnerMessage(typeof raw === "string" ? raw : undefined);
      if (normalized?.message) {
        detailMessage = normalized.message;
      }
      if (normalized?.url) {
        urlLine = this.formatErrorUrl(normalized.url);
      }
    }

    const ordered = [titleLine || fallbackTitle, detailMessage, urlLine];
    const compact = ordered.filter((line, index, self) => !!line && self.indexOf(line) === index) as string[];
    return compact.length > 0 ? compact.join("\n") : fallbackTitle;
  }

  private isFileAccepted(file: UploadFile): boolean {
    if (this.allowAllFormats) {
      return true;
    }

    if (!this.acceptRules.length) {
      return true;
    }

    const fileType = (file.nativeFile?.type || file.type || "").toLowerCase();
    const fileName = (file.nativeFile?.name || file.name || "").toLowerCase();
    const extension = this.extractExtension(fileName ? fileName : undefined);
    const extensionWithDot = extension ? `.${extension}` : "";

    return this.acceptRules.some((rule) => {
      switch (rule.type) {
        case "mime":
          return !!fileType && fileType === rule.value;
        case "wildcard":
          return !!fileType && fileType.startsWith(`${rule.value}/`);
        case "extension":
          return extensionWithDot === rule.value;
        default:
          return false;
      }
    });
  }

  private updateAcceptConstraints(): void {
    const acceptValue = (this.accept || "").trim();
    if (!acceptValue) {
      this.acceptRules = [];
      this.allowAllFormats = true;
      this.options = {...this.options, allowedContentTypes: undefined};
      this.allowedFormatsDescription = undefined;
      return;
    }

    const parts = acceptValue.split(",").map((part) => part.trim()).filter((part) => part.length > 0);
    if (!parts.length || parts.includes("*/*")) {
      this.acceptRules = [];
      this.allowAllFormats = true;
      this.options = {...this.options, allowedContentTypes: undefined};
      this.allowedFormatsDescription = undefined;
      return;
    }

    const rules: AcceptRule[] = [];
    const mimeTypes: string[] = [];

    parts.forEach((original) => {
      const normalized = original.toLowerCase();
      if (!normalized) {
        return;
      }
      if (normalized.endsWith("/*")) {
        const group = normalized.split("/")[0];
        if (group) {
          rules.push({raw: original, type: "wildcard", value: group});
        }
        return;
      }
      if (normalized.includes("/")) {
        rules.push({raw: original, type: "mime", value: normalized});
        mimeTypes.push(normalized);
        return;
      }
      if (normalized.startsWith(".")) {
        rules.push({raw: original, type: "extension", value: normalized});
        return;
      }
      rules.push({raw: original, type: "extension", value: `.${normalized}`});
    });

    this.acceptRules = rules;
    this.allowAllFormats = rules.length === 0;
    this.options = {...this.options, allowedContentTypes: mimeTypes.length ? mimeTypes : undefined};
    this.allowedFormatsDescription = this.composeAllowedFormatsDescription();
  }

  private composeAllowedFormatsDescription(): string | undefined {
    if (this.allowAllFormats) {
      return undefined;
    }
    if (this.acceptRules.length === 0) {
      const trimmed = this.accept?.trim();
      return trimmed && trimmed !== "*/*" ? trimmed : undefined;
    }
    return this.acceptRules.map((rule) => rule.raw).join(", ");
  }

  private extractInnerMessage(error: any): string | undefined {
    const candidates = [error?.error?.message, error?.error?.error, error?.error, error?.message];
    for (const candidate of candidates) {
      if (typeof candidate === "string" && candidate.trim()) {
        const trimmed = candidate.trim();
        if (/^Http failure response for/i.test(trimmed)) {
          continue;
        }
        return trimmed;
      }
    }
    return undefined;
  }

  private truncateUrl(url: string): string {
    const maxLength = 140;
    if (url.length <= maxLength) {
      return url;
    }
    return `${url.slice(0, maxLength - 1)}…`;
  }

  private normalizeInnerMessage(raw?: string): {message?: string; url?: string} | undefined {
    if (!raw) {
      return undefined;
    }
    let message = raw.trim();
    let extractedUrl: string | undefined;

    const forMatch = message.match(/\bfor\s+(https?:\/\/\S+)/i);
    if (forMatch) {
      extractedUrl = forMatch[1];
      message = message.replace(forMatch[0], "").trim();
    }

    const genericMatch = message.match(/(https?:\/\/\S+)/i);
    if (!extractedUrl && genericMatch) {
      extractedUrl = genericMatch[1];
      message = message.replace(genericMatch[1], "").trim();
    }

    message = message.replace(/[\s:]+$/, "").replace(/\s{2,}/g, " ");

    if (/^http failure during parsing$/i.test(message)) {
      message = "Unable to read server response.";
    }

    return {
      message: message || undefined,
      url: extractedUrl,
    };
  }

  private formatErrorUrl(url: string): string {
    if (!url) {
      return url;
    }
    const trimmed = url.replace(/[.,;]+$/, "");
    try {
      return this.simplifyUrl(trimmed);
    } catch (_) {
      return this.truncateUrl(trimmed);
    }
  }
}
